import { ConfigStateService, LocalizationService } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngxs/store';
import { map } from 'rxjs/operators';
import { ConfigurationSettingsDto } from 'src/core/models/configuration-setting/configuration-settings.dto';
import { DeletionScopeType } from 'src/core/models/generic-lookup-type/conversation/deletion-scope-type.glt';
import { GenericLookupDto } from 'src/core/models/generic-lookup/generic-lookup.dto';
import { ConfigurationSettingsService } from 'src/core/services/configuration-settings/configuration-settings.service';
import { GenericLookupTypeState } from 'src/core/states/generic-lookup-type/generic-lookup-type.state';
import { IntegerValidator } from 'src/core/validators/shared/integer.validator';
import { FeatureService } from 'src/core/services/feature/feature.service';
import { FeatureConstants } from 'src/core/constants/feature-constant';

@Component({
    selector: 'ca-auto-deletion-settings',
    templateUrl: './auto-deletion-settings.component.html',
    styleUrls: ['./auto-deletion-settings.component.scss'],
    standalone: false
})
export class AutoDeletionSettingsComponent implements OnInit {
  form: FormGroup;
  deletionScopes: GenericLookupDto[];
  autoDeletionOptionMaxValue: number = 120;
  callRecorderEnabled: boolean = false;

  get autoDeletionDisabled(): boolean {
    return !this.form?.controls['autoDeletionEnabled']?.value;
  }

  private readonly prefix = 'Conversation';
  private readonly autoDeletionEnabled = `${this.prefix}.AutoDeletionEnabled`;
  private readonly deletionScopeType = `${this.prefix}.DeletionScopeType`;
  private readonly autoDeletionOption = `${this.prefix}.AutoDeletionOption`;
  private readonly autoDeletionOptionMax = `${this.prefix}.AutoDeletionOptionMax`;

  constructor(
    private fb: FormBuilder,
    private store: Store,
    private config: ConfigStateService,
    private settingService: ConfigurationSettingsService,
    private toastr: ToasterService,
    private localizationService: LocalizationService,
    private featureService: FeatureService
  ) {
    const autoDeletionOptionMaxValue = Number(this.config.getSetting(this.autoDeletionOptionMax));
    this.autoDeletionOptionMaxValue = isNaN(autoDeletionOptionMaxValue)
      ? this.autoDeletionOptionMaxValue
      : autoDeletionOptionMaxValue;
    this.callRecorderEnabled = this.featureService.isEnabled(FeatureConstants.CallRecorder);

    this.form = this.fb.group({
      autoDeletionEnabled: [
        JSON.parse(this.config.getSetting(this.autoDeletionEnabled).toLowerCase()),
      ],
      autoDeletionOption: [
        this.config.getSetting(this.autoDeletionOption),
        {
          validators: [
            Validators.required,
            IntegerValidator.minMax(1, this.autoDeletionOptionMaxValue),
          ],
        },
      ],
      deletionScopeType: [this.config.getSetting(this.deletionScopeType)],
    });

    this.store
      .select(GenericLookupTypeState.getGenericLookups)
      .pipe(map(filterFn => filterFn(DeletionScopeType)))
      .subscribe(result => {
        this.deletionScopes = result;
      });

    this.form.controls['autoDeletionEnabled'].valueChanges.subscribe(value => {
      if (value === true) {
        this.form?.controls['autoDeletionOption'].enable();
        this.form?.controls['deletionScopeType'].enable();
        return;
      }

      this.form?.controls['autoDeletionOption'].disable();
      this.form?.controls['deletionScopeType'].disable();
    });
  }

  ngOnInit(): void {
    if (this.autoDeletionDisabled === true) {
      this.form?.controls['autoDeletionOption'].disable();
      this.form?.controls['deletionScopeType'].disable();
    }
  }

  onSubmitForm(eventArgs): void {
    if (!this.form.valid) {
      return;
    }

    const settings: ConfigurationSettingsDto[] = [];

    settings.push({
      settingName: this.autoDeletionEnabled,
      settingValue: this.form.get('autoDeletionEnabled').value.toString(),
    });

    settings.push({
      settingName: this.deletionScopeType,
      settingValue: this.form.get('deletionScopeType').value.toString(),
    });

    settings.push({
      settingName: this.autoDeletionOption,
      settingValue: this.form.get('autoDeletionOption').value.toString(),
    });

    this.settingService.saveSetting(settings).subscribe(res => {
      this.toastr.success(
        this.localizationService.instant('AbpSettingManagement::SavedSuccessfully')
      );
    });
  }
}
